import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ArrowDown } from "lucide-react";
import { IMAGES, HELP_PATHS, DIFFERENCE_STEPS, RENO_PROJECTS, PROCESS_STEPS, BEFORE_AFTER } from "../mock";
import { ButtonLink, Kicker, SectionHeading } from "../components/UI";
import BeforeAfterSlider from "../components/BeforeAfterSlider";
import { useReveal } from "../hooks/useReveal";
import { useSiteImages } from "../context/SiteImages";
import { api } from "../lib/api";
import homeHero from "../assets/home-hero.png";

export default function Home() {
  const ref = useReveal();
  const { simg } = useSiteImages();
  const [texts, setTexts] = React.useState({});
  React.useEffect(() => {
    api.get("/site-texts").then((res) => setTexts(res.data || {})).catch(() => {});
  }, []);
  const txt = (k, fallback) => (texts[k] != null && texts[k] !== "" ? texts[k] : fallback);
  return (
    <div ref={ref}>
      {/* HERO */}
      <section className="relative min-h-screen flex items-end overflow-hidden">
        <div className="absolute inset-0">
          <img src={simg("hero_home", homeHero)} alt="Elegant finished home interior" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-navy/85 via-navy/30 to-navy/50" />
        </div>
        <div className="relative z-10 w-full max-w-[1440px] mx-auto px-6 lg:px-12 pb-20 md:pb-28">
          <div className="max-w-3xl">
            <Kicker dark className="reveal">Real Estate · Design · Renovation</Kicker>
            <h1 className="reveal font-serif font-medium text-cream text-5xl md:text-7xl lg:text-[5.2rem] leading-[1.02] mt-6">
              Find It. Design It.<br /><span className="text-gold-soft italic">Transform It.</span>
            </h1>
            <p className="reveal font-sans text-cream/85 text-lg md:text-xl leading-relaxed mt-8 max-w-2xl font-light">
              One team to help you find the right property, design your vision, and bring the renovation to life.
            </p>
            <div className="reveal flex flex-col sm:flex-row gap-4 mt-10">
              <ButtonLink to="/contact" variant="gold">Start Your Project</ButtonLink>
              <ButtonLink to="/find-renovate" variant="outlineLight">Find Your Property</ButtonLink>
            </div>
          </div>
        </div>
        <div className="absolute bottom-8 right-8 z-10 hidden md:flex items-center gap-3 text-cream/70">
          <span className="font-sans text-[11px] tracking-luxe uppercase">Scroll</span>
          <ArrowDown size={16} className="animate-bounce" />
        </div>
      </section>

      {/* INTRO STATEMENT */}
      <section className="py-24 md:py-32 bg-cream">
        <div className="max-w-4xl mx-auto px-6 lg:px-12 text-center">
          <p className="reveal font-serif text-3xl md:text-4xl lg:text-[2.75rem] leading-[1.35] text-navy font-light">
            Buying a home that needs work shouldn't feel overwhelming. Neither should renovating the home you already own. We combine <span className="text-gold italic">real estate expertise</span>, thoughtful design, and renovation management to help you create a home that's truly yours.
          </p>
        </div>
      </section>

      {/* TWO WAYS WE CAN HELP */}
      <section className="pb-24 md:pb-32 bg-cream">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12">
          <SectionHeading kicker="What's Your Starting Point?" title="Two Ways We Can Help" className="reveal mb-16" />
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {HELP_PATHS.map((p) => (
              <div key={p.id} className="reveal group bg-white flex flex-col">
                <div className="img-zoom h-72 md:h-80">
                  <img src={simg(`home_help_${p.id}`, p.image)} alt={p.title} className="w-full h-full object-cover" />
                </div>
                <div className="p-8 md:p-10 flex flex-col flex-1">
                  <Kicker>{p.kicker}</Kicker>
                  <h3 className="font-serif text-3xl md:text-[2.1rem] text-navy leading-tight mt-4">{p.title}</h3>
                  <p className="font-sans text-charcoal/70 leading-relaxed mt-4 flex-1">{p.body}</p>
                  <Link to={p.to} className="group/link inline-flex items-center gap-3 mt-7 font-sans text-[12px] tracking-wide-nav uppercase text-navy">
                    <span className="link-underline">{p.cta}</span>
                    <ArrowRight size={16} className="transition-transform duration-300 group-hover/link:translate-x-1 text-gold" />
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* THE DIFFERENCE */}
      <section className="py-24 md:py-32 bg-navy text-cream">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
            <div className="lg:col-span-5">
              <SectionHeading dark kicker="The Difference" title="Real Estate Meets Design + Renovation" className="reveal" />
              <p className="reveal font-sans text-cream/70 leading-relaxed mt-8 text-lg font-light">
                Most buyers have to work with multiple disconnected professionals — a realtor to buy, a designer to imagine, a contractor to build — and you're left coordinating everyone.
              </p>
              <p className="reveal font-serif italic text-gold-soft text-2xl mt-6">We do things differently. We connect the entire process.</p>
            </div>
            <div className="lg:col-span-6 lg:col-start-7 grid grid-cols-1 sm:grid-cols-2 gap-px bg-cream/10">
              {DIFFERENCE_STEPS.map((s) => (
                <div key={s.num} className="reveal bg-navy p-8 md:p-10 hover:bg-navy-light transition-colors duration-500">
                  <span className="font-serif text-gold text-4xl">{s.num}</span>
                  <h3 className="font-serif text-2xl text-cream mt-3">{s.title}</h3>
                  <p className="font-sans text-cream/65 text-sm leading-relaxed mt-3">{s.text}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* WHAT YOU SEE / WHAT WE SEE */}
      <section className="py-24 md:py-32 bg-cream">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12">
          <div className="max-w-3xl reveal">
            <Kicker>See The Potential First</Kicker>
            <h2 className="font-serif font-medium text-navy text-4xl md:text-5xl lg:text-[3.4rem] leading-[1.08] mt-4">
              The Perfect Home Might Not Look Perfect Yet.
            </h2>
            <p className="font-sans text-charcoal/70 leading-relaxed mt-6 text-lg font-light">
              Some of the best opportunities are hidden behind dated finishes and closed-off layouts. Drag the slider to see what we see.
            </p>
          </div>
          <div className="reveal mt-14">
            <BeforeAfterSlider before={simg("home_reveal_before", BEFORE_AFTER[0].before)} after={simg("home_reveal_after", BEFORE_AFTER[0].after)} className="h-[420px] md:h-[600px]" />
          </div>
          <div className="reveal mt-10">
            <ButtonLink to="/find-renovate" variant="navy">Find My Fixer-Upper</ButtonLink>
          </div>
        </div>
      </section>

      {/* HOMEOWNER RENOVATIONS */}
      <section className="py-24 md:py-32 bg-cream-dark">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-14 items-center">
            <div className="lg:col-span-6 img-zoom reveal">
              <img src={simg("home_homeowner", IMAGES.livingRoom3)} alt="Reimagined living space" className="w-full h-[520px] object-cover" />
            </div>
            <div className="lg:col-span-6">
              <SectionHeading kicker="Homeowner Renovations" title="Your Home. Reimagined." className="reveal" />
              <p className="reveal font-sans text-charcoal/70 leading-relaxed mt-6 text-lg font-light">
                Moving isn't always the answer. Sometimes the right home is the one you're already living in. Whether it feels outdated, dysfunctional, or simply doesn't reflect your style, we help uncover what's possible.
              </p>
              <div className="reveal grid grid-cols-2 gap-x-6 gap-y-3 mt-8">
                {RENO_PROJECTS.map((p) => (
                  <div key={p} className="flex items-center gap-3">
                    <span className="w-1.5 h-1.5 bg-gold rotate-45 shrink-0" />
                    <span className="font-sans text-sm text-charcoal/80">{p}</span>
                  </div>
                ))}
              </div>
              <div className="reveal mt-9">
                <ButtonLink to="/design-renovation" variant="navy">Talk About My Home</ButtonLink>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* BEFORE + AFTER GALLERY */}
      <section className="py-24 md:py-32 bg-cream">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12">
          <SectionHeading kicker="Before + After" title="From What It Was to What It Could Be" center className="reveal max-w-2xl mb-4" />
          <p className="reveal text-center font-sans text-charcoal/65 max-w-2xl mx-auto mb-16 font-light">
            Explore how we've transformed dated, underused spaces into homes designed around the people who live there.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {BEFORE_AFTER.map((b, i) => (
              <div key={b.id} className="reveal">
                <BeforeAfterSlider before={simg(`home_ba${i + 1}_before`, b.before)} after={simg(`home_ba${i + 1}_after`, b.after)} beforeLabel="Before" afterLabel="After" className="h-80" />
                <h4 className="font-serif text-2xl text-navy mt-5">{txt(`home_ba${i + 1}_title`, b.title)}</h4>
                <p className="font-sans text-[11px] tracking-luxe uppercase text-gold mt-1">{txt(`home_ba${i + 1}_location`, b.location)}</p>
              </div>
            ))}
          </div>
          <div className="reveal text-center mt-14">
            <ButtonLink to="/portfolio" variant="outline" className="!inline-flex">View Our Projects</ButtonLink>
          </div>
        </div>
      </section>

      {/* PROCESS */}
      <section className="py-24 md:py-32 bg-navy text-cream">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12">
          <SectionHeading dark kicker="Our Process" title="From First Conversation to Finished Home" center className="reveal max-w-2xl mb-16" />
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-cream/10">
            {PROCESS_STEPS.map((s) => (
              <div key={s.num} className="reveal bg-navy p-8 md:p-10 hover:bg-navy-light transition-colors duration-500 group">
                <div className="flex items-baseline gap-4">
                  <span className="font-serif text-gold text-5xl">{s.num}</span>
                  <h3 className="font-serif text-2xl text-cream">{s.title}</h3>
                </div>
                <p className="font-sans text-cream/65 text-sm leading-relaxed mt-4">{s.text}</p>
              </div>
            ))}
          </div>
          <div className="reveal text-center mt-14">
            <ButtonLink to="/process" variant="gold">See How It Works</ButtonLink>
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="relative py-28 md:py-40 overflow-hidden">
        <div className="absolute inset-0">
          <img src={simg("home_cta", IMAGES.kitchen1)} alt="Finished kitchen" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-navy/80" />
        </div>
        <div className="relative z-10 max-w-4xl mx-auto px-6 text-center">
          <h2 className="reveal font-serif font-medium text-cream text-4xl md:text-6xl leading-[1.08]">
            You Don't Need the Perfect Home. You Need the Right Team to <span className="text-gold-soft italic">Help You Create It.</span>
          </h2>
          <p className="reveal font-sans text-cream/80 text-lg mt-8 font-light max-w-2xl mx-auto">
            Whether you're starting with a real estate search or the house you're already living in, we'd love to hear what you're envisioning.
          </p>
          <div className="reveal flex justify-center mt-10">
            <ButtonLink to="/contact" variant="gold">Start Your Project</ButtonLink>
          </div>
        </div>
      </section>
    </div>
  );
}
